/**
 * Static map previews - a Mapbox Static Images URL for a box of pins.
 *
 * A preview is a picture: it shows where things are and taps through to the
 * real map. Rendering it as an image instead of a Mapbox GL instance costs no
 * WebGL context, no worker pool and no tile cache, and the browser caches it
 * like any other image. The one live map in the app is the map page (and a
 * preview opened fullscreen).
 *
 * The camera mirrors the live preview: a single point sits at zoom 12; two or
 * more points are fitted with padding. Styles match the live maps.
 */

import { MAP_STYLE_IDS, type MapTheme } from "./mapStyles.ts";

export interface StaticMapPoint {
  lat: number;
  lng: number;
}

/**
 * A custom pin. The API draws marker images at their native pixel size with
 * the bottom centre on the coordinate, so a @2x image needs a 2x file, and
 * the fit needs headroom above the top-most point for the pin's height.
 */
export interface StaticMapPin {
  /** Public PNG at 1x. */
  url: string;
  /** Public PNG at 2x. */
  url2x: string;
  /** Size of the 1x image, in CSS pixels. */
  width: number;
  height: number;
}

export interface StaticMapImageOptions {
  token: string;
  theme: MapTheme;
  /** Box size in CSS pixels. */
  width: number;
  height: number;
  places: StaticMapPoint[];
  user?: StaticMapPoint | null;
  /** Public PNGs for the place pin; without them the built-in Mapbox pin is used. */
  placePin?: StaticMapPin;
  /** A single point's zoom, when it isn't the preview's default. */
  zoom?: number;
  /**
   * Space between the edge and any pin, in CSS pixels. The fit's padding is
   * shaped like a pin (its height above the point, half its width beside), so
   * a short box keeps most of its height for the map. Two or more points only.
   */
  margin?: number;
  /**
   * The screen's device pixel ratio. The image is requested with enough
   * pixels to fill the box at device resolution, so it is never upscaled:
   * `@2x` above 1, and a larger canvas above 2 (the API has no @3x). The
   * API caps each side at 1280 pixels.
   */
  devicePixelRatio?: number;
}

export const MAPBOX_STATIC_MAX_PX = 1280;
export const PLACE_PIN_COLOR = "fd6e12";
export const USER_PIN_COLOR = "1a7fd9";
export const SINGLE_POINT_ZOOM = 12;
export const DEFAULT_MARGIN = 16;
/** Mapbox's built-in `pin-l`, roughly, in CSS pixels. */
const BUILTIN_PIN = { width: 36, height: 50 };

const round = (value: number): number => Math.round(value * 1e5) / 1e5;
const at = (point: StaticMapPoint): string => `(${round(point.lng)},${round(point.lat)})`;

const clampSide = (value: number, scale: number): number =>
  Math.max(1, Math.min(Math.round(value), Math.floor(MAPBOX_STATIC_MAX_PX / scale)));

/** Null when there is nothing to show. */
export const staticMapImageUrl = ({
  token,
  theme,
  width,
  height,
  places,
  user,
  placePin,
  zoom = SINGLE_POINT_ZOOM,
  margin = DEFAULT_MARGIN,
  devicePixelRatio = 1,
}: StaticMapImageOptions): string | null => {
  const points = user ? [user, ...places] : places;
  if (points.length === 0) return null;

  const dpr = Math.max(1, devicePixelRatio);
  const retina = dpr > 1;
  const scale = retina ? 2 : 1;

  const placeMarker = placePin ? `url-${encodeURIComponent(retina ? placePin.url2x : placePin.url)}` : `pin-l+${PLACE_PIN_COLOR}`;
  const overlays = [
    ...(user ? [`pin-s+${USER_PIN_COLOR}${at(user)}`] : []),
    ...places.map(place => `${placeMarker}${at(place)}`),
  ].join(",");

  const camera =
    points.length === 1 ? `${round(points[0].lng)},${round(points[0].lat)},${Math.round(zoom * 10) / 10}` : "auto";

  const size = `${clampSide((width * dpr) / scale, scale)}x${clampSide((height * dpr) / scale, scale)}${retina ? "@2x" : ""}`;

  const query = new URLSearchParams();
  if (points.length > 1) {
    // top,right,bottom,left — the pin stands on its point and rises above it.
    const pin = placePin ?? BUILTIN_PIN;
    const side = Math.ceil(pin.width / 2) + margin;
    query.set("padding", `${pin.height + margin},${side},${margin},${side}`);
  }
  query.set("attribution", "false");
  query.set("access_token", token);

  return `https://api.mapbox.com/styles/v1/${MAP_STYLE_IDS[theme]}/static/${overlays}/${camera}/${size}?${query}`;
};
